// temp allow until we comment back in the blog CTA
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-lines */
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ReactComponent as ArrowSVG } from "images/global/arrow.svg"
import { ReactComponent as FooterLogo } from "images/global/FooterLogo.svg"
import { ReactComponent as InstagramSVG } from "images/global/instagram.svg"
import { ReactComponent as LinkedinSVG } from "images/global/linkedin.svg"
import { ReactComponent as TwitterSVG } from "images/global/twitter.svg"
import { DesktopTabletOnly, MobileOnly } from "library/breakpointUtils"
import useCanHover from "library/canHover"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import UniversalLink from "library/Loader/UniversalLink"
import useAnimation from "library/useAnimation"
import { useContext, useRef } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import media from "styles/media"
import textStyles from "styles/text"
import links from "utils/links"

import { ModalContext } from "./Providers/OpenModal"

export default function Footer() {
  const wrapperRef = useRef<HTMLElement | null>(null)
  const logoRef = useRef<HTMLDivElement | null>(null)
  const rectangle = useRef<HTMLDivElement | null>(null)
  const { setOpenModal } = useContext(ModalContext)

  const DESK_LINKS = [
    // {
    //   to: links.about,
    //   content: "About Us",
    // },
    // {
    //   to: links.blog,
    //   content: "Read Our Blog",
    // },
    {
      onClick: () => setOpenModal(true),
      content: "Get Early Access",
    },
    {
      to: links.contact,
      content: "Contact Us",
    },
  ]

  const MOB_LINKS = [
    {
      onClick: () => setOpenModal(true),
      content: "Get Early Access",
    },
    {
      to: links.contact,
      content: "Contact Us",
    },
  ]

  const canHover = useCanHover()
  const footerOnScreen = useAnimation(() => {
    const out = { value: false }
    ScrollTrigger.create({
      trigger: wrapperRef.current,
      start: "top 90%",
      onEnter: () => {
        out.value = true
      },
      onLeaveBack: () => {
        out.value = false
      },
    })
    return out
  }, [])

  useAnimation(
    () => {
      if (!canHover) {
        gsap
          .timeline({
            repeat: -1,
            repeatDelay: 3,
          })
          .fromTo(
            rectangle.current,
            {
              xPercent: -150,
              yPercent: -50,
            },
            {
              xPercent: 250,
              yPercent: -50,
              duration: 3,
              ease: "linear",
            },
          )

        return
      }

      if (!logoRef.current) return
      const xTo = gsap.quickTo(rectangle.current, "x", { duration: 1 })
      const yTo = gsap.quickTo(rectangle.current, "y", { duration: 1 })
      const xpTo = gsap.quickTo(rectangle.current, "xPercent", { duration: 1 })
      const ypTo = gsap.quickTo(rectangle.current, "yPercent", { duration: 1 })

      const onPointerMove = (e: { clientX: number; clientY: number }) => {
        // get the position of the mouse relative to the top left of the logo
        const bounds = logoRef.current?.getBoundingClientRect()
        if (!bounds) return
        const x = e.clientX - bounds.left
        const y = e.clientY - bounds.top

        xTo(x)
        yTo(y)
        xpTo(-50)
        ypTo(-50)
      }

      const onScroll = () => {
        if (!footerOnScreen?.value && logoRef.current) {
          xTo(logoRef.current.clientWidth / 2)
          yTo(logoRef.current.clientHeight / 2)
          xpTo(-50)
          ypTo(-50)
        }
      }

      window.addEventListener("pointermove", onPointerMove)
      window.addEventListener("scroll", onScroll)
      return () => {
        window.removeEventListener("pointermove", onPointerMove)
        window.removeEventListener("scroll", onScroll)
      }
    },
    [footerOnScreen, canHover],
    { scope: wrapperRef.current },
  )

  return (
    <Wrapper ref={wrapperRef}>
      <Inner>
        <ClipperImage>
          {/* We put this inline because SVGO would round the coordinates otherwise */}
          <svg>
            <clipPath id="clipper" clipPathUnits="objectBoundingBox">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.959189 0.186735H0.999233L0.941011 0.106291L0.995018 0.0538049H0.952866L0.902811 0.101016V0H0.869353V0.186735H0.902811V0.14348L0.918355 0.128182L0.959189 0.186735ZM0.122187 0.186735H0.000737055V0.00210999H0.036566V0.155612H0.122187V0.186735ZM0.250766 0.115786C0.250766 0.120534 0.250502 0.126336 0.250239 0.128974H0.151709C0.154607 0.149019 0.167516 0.160887 0.186484 0.160887C0.202028 0.160887 0.213092 0.153502 0.216517 0.140579L0.247604 0.149019C0.240228 0.174075 0.217044 0.188845 0.185957 0.188845C0.141962 0.188845 0.118515 0.154821 0.118515 0.12027C0.118515 0.0857186 0.140117 0.0516949 0.184113 0.0516949C0.228899 0.0516949 0.250766 0.0849274 0.250766 0.115786ZM0.152499 0.104709H0.2152C0.213619 0.0888836 0.203345 0.0770149 0.184113 0.0770149C0.167779 0.0770149 0.156188 0.0867736 0.152499 0.104709ZM0.351485 0.0538049V0.0672562C0.344108 0.0580249 0.330409 0.0516949 0.314075 0.0516949C0.275875 0.0516949 0.252955 0.0780699 0.252955 0.117369C0.252955 0.156404 0.275875 0.182779 0.314866 0.182779C0.330673 0.182779 0.345162 0.176712 0.351748 0.167217V0.196004C0.351748 0.213148 0.338576 0.224489 0.318554 0.224489C0.303011 0.224489 0.290365 0.217104 0.288784 0.204708L0.257961 0.209719C0.26244 0.234512 0.287467 0.251128 0.318554 0.251128C0.358071 0.251128 0.384416 0.227654 0.384416 0.192575V0.0538049H0.351485ZM0.353593 0.117369C0.353593 0.139524 0.340157 0.15403 0.319608 0.15403C0.299322 0.15403 0.285886 0.139524 0.285886 0.117369C0.285886 0.0949499 0.299322 0.0804436 0.319608 0.0804436C0.340157 0.0804436 0.353593 0.0949499 0.353593 0.117369ZM0.442566 0.186735L0.388822 0.00210999H0.425705L0.461271 0.144007L0.497363 0.00210999H0.537934L0.57429 0.144007L0.609855 0.00210999H0.646738L0.593521 0.186735H0.555585L0.517912 0.0514312L0.479975 0.186735H0.442566ZM0.630406 0.12027C0.630406 0.159832 0.659386 0.188845 0.699693 0.188845C0.740001 0.188845 0.76898 0.159832 0.76898 0.12027C0.76898 0.0807074 0.740001 0.0516949 0.699693 0.0516949C0.659386 0.0516949 0.630406 0.0807074 0.630406 0.12027ZM0.735522 0.12027C0.735522 0.144007 0.721032 0.160096 0.699693 0.160096C0.67809 0.160096 0.663864 0.144007 0.663864 0.12027C0.663864 0.0965324 0.67809 0.0804436 0.699693 0.0804436C0.721032 0.0804436 0.735522 0.0965324 0.735522 0.12027ZM0.865863 0.0538049L0.863755 0.0859824C0.859013 0.0838724 0.85269 0.0828174 0.846894 0.0828174C0.82819 0.0828174 0.811329 0.0983786 0.811329 0.137941V0.186735H0.778135V0.0538049H0.811329V0.0780699C0.817915 0.0630362 0.835303 0.0516949 0.851373 0.0516949C0.856115 0.0516949 0.861911 0.0524862 0.865863 0.0538049ZM0 0.208233H0.24679V0.206729H0V0.208233ZM0.39515 0.208233H1V0.206729H0.39515V0.208233Z"
                fill="white"
              />
            </clipPath>
          </svg>
        </ClipperImage>
        <LogoWrapper ref={logoRef}>
          <LogoClipped>
            <Rectangle ref={rectangle} />
          </LogoClipped>
        </LogoWrapper>
        <Top>
          <Left>
            <Copyright>© 2023 LegWork App, Co.</Copyright>
            <Text>The First Ever AI Driven Talent Tracking System.</Text>
          </Left>
          <Right>
            <DesktopTabletOnly>
              <LinkColumn>
                {DESK_LINKS.map((link, index) => {
                  if (link.onClick) {
                    return (
                      <Link
                        type="button"
                        ariaLabel={link.content}
                        onClick={link.onClick}
                        key={index}
                      >
                        <Arrow />
                        {link.content}
                      </Link>
                    )
                  }
                  return (
                    <Link to={link.to} key={index}>
                      <Arrow />
                      {link.content}
                    </Link>
                  )
                })}
              </LinkColumn>
            </DesktopTabletOnly>
            <MobileOnly>
              <LinkColumn>
                {MOB_LINKS.map((link, index) => {
                  if (link.onClick) {
                    return (
                      <Link
                        type="button"
                        ariaLabel={link.content}
                        onClick={link.onClick}
                        key={index}
                      >
                        <Arrow />
                        {link.content}
                      </Link>
                    )
                  }
                  return (
                    <Link to={link.to} key={index}>
                      <Arrow />
                      {link.content}
                    </Link>
                  )
                })}
              </LinkColumn>
            </MobileOnly>
            <LinkColumn>
              <Link to={links.terms}>
                <Arrow />
                Terms and Conditions
              </Link>
              <Link to={links.privacy}>
                <Arrow />
                Privacy Policy
              </Link>
            </LinkColumn>
          </Right>
        </Top>
        <Bottom>
          {/* temp div to preserve spacing */}
          <div />
          {/* <BlogCTA to={links.blog}>
            <Story>
              Read our story <Arrow />
            </Story>
            <Why>
              <ConstantMarquee>
                Why We Built This and Why It Matters.&nbsp;
              </ConstantMarquee>
            </Why>
          </BlogCTA> */}
          <Socials>
            <UniversalLink to={links.twitter}>
              <Twitter />
            </UniversalLink>
            <UniversalLink to={links.instagram}>
              <Instagram />
            </UniversalLink>
            <UniversalLink to={links.linkedin}>
              <Linkedin />
            </UniversalLink>
          </Socials>
        </Bottom>
      </Inner>
    </Wrapper>
  )
}

const Wrapper = styled.footer`
  width: 100%;
  position: relative;
  background-color: ${colors.gray600};
  overflow: clip;

  ${fresponsive(css`
    height: 738px;
    padding: 20px;
  `)}

  ${media.fullWidth} {
    height: 51.25vw;
    padding: 1.389vw;
  }

  ${ftablet(css`
    height: 620px;
  `)}

  ${fmobile(css`
    height: 640px;
    padding: 0;
  `)}
`

const Inner = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  background-color: ${colors.black700};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${fresponsive(css`
    border-radius: 20px;
    padding: 377px 30px 30px;
  `)}

  ${media.fullWidth} {
    border-radius: 1.389vw;
    padding: 26.181vw 2.083vw 2.083vw;
  }

  ${ftablet(css`
    padding: 259px 40px 32px;
  `)}

  ${fmobile(css`
    border-radius: 30px 30px 0 0;
    padding: 54px 19px 42px;
  `)}
`

const ClipperImage = styled.svg`
  opacity: 0;
  position: absolute;
  pointer-events: none;
`

const LogoWrapper = styled.div`
  position: absolute;

  ${fresponsive(css`
    width: 1330px;
    height: 334px;
    left: 30px;
    top: 67px;
  `)}

  ${media.fullWidth} {
    width: 92.361vw;
    height: 23.194vw;
    left: 2.083vw;
    top: 4.653vw;
  }

  ${ftablet(css`
    width: 904px;
    height: 227px;
    top: 54px;
    left: 39px;
  `)}

  ${fmobile(css`
    width: 355px;
    height: 89px;
    position: relative;
    top: 0;
    left: 0;
  `)}
`

const LogoClipped = styled.div`
  width: 100%;
  aspect-ratio: 1 /1;
  background: white;
  clip-path: url("#clipper");
`

const Rectangle = styled.div`
  ${fresponsive(css`
    width: 60%;
    height: 60%;
    background: linear-gradient(
      270deg,
      rgb(182 65 237 / 100%) 8.19%,
      rgb(64 244 255 / 100%) 49.77%,
      rgb(37 211 107 / 100%) 92.22%
    );
    filter: blur(107px);
  `)}

  ${ftablet(css`
    filter: blur(75px);
  `)}

  ${fmobile(css`
    filter: blur(27px);
  `)}
`

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  z-index: 2;

  ${fmobile(css`
    flex-direction: column;
    justify-content: unset;
    gap: 47px;
  `)}
`

const Left = styled.div`
  display: flex;
  flex-direction: column;

  ${fresponsive(css`
    gap: 38px;
  `)}

  ${fmobile(css`
    gap: 47px;
  `)}
`

const Copyright = styled.span`
  ${textStyles.bodySmall}
  color: ${colors.grayWhite};
`

const Text = styled.p`
  ${textStyles.h3}
  color: ${colors.grayWhite};

  ${fresponsive(css`
    width: 398px;
  `)}

  ${fmobile(css`
    width: 100%;
  `)}
`

const Right = styled.div`
  display: flex;
  align-items: flex-start;

  ${fresponsive(css`
    gap: 78px;
  `)}

  ${fmobile(css`
    flex-direction: column;
    gap: 20px;
  `)}
`

const LinkColumn = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  ${fresponsive(css`
    gap: 10px;
  `)}

  ${fmobile(css`
    gap: 20px;
  `)}
`

const Arrow = styled(ArrowSVG)`
  height: auto;
  transition: rotate 0.5s;

  ${fresponsive(css`
    width: 9.5px;
  `)}
`

const Icon = css`
  height: auto;

  ${fresponsive(css`
    width: 18px;
  `)}
`

const Link = styled(UniversalLink)`
  ${textStyles.bodySmall}
  color: ${colors.grayWhite};
  display: flex;
  align-items: center;

  &:hover {
    ${Arrow} {
      rotate: -45deg;
    }
  }

  ${fresponsive(css`
    gap: 7px;
  `)}
`

const Bottom = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  ${fmobile(css`
    justify-content: start;
  `)}
`

const BlogCTA = styled(UniversalLink)`
  overflow: hidden;
  display: flex;
  align-items: center;

  ${fresponsive(css`
    width: 434px;
    height: 29px;
    border-radius: 50px;
  `)}

  ${fmobile(css`
    display: none;
  `)}
`

const Story = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #ad85ef;
  background: #d5bdff;
  ${textStyles.kickerOne}
  color: #4F29BB;

  ${Arrow} {
    path {
      stroke: #4f29bb;
    }
  }

  &:hover {
    ${Arrow} {
      rotate: -45deg;
    }
  }

  ${fresponsive(css`
    width: 155px;
    padding: 0 15px;
    gap: 10px;
  `)}
`

const Why = styled.div`
  flex-grow: 1;
  background: #f7f8fa;
  height: 100%;
  ${textStyles.kickerOne}
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const Socials = styled.div`
  display: flex;
  align-items: center;

  ${fresponsive(css`
    gap: 43px;
  `)}

  ${fmobile(css`
    width: 189px;
    justify-content: space-between;
    gap: 0;
  `)}
`

const Twitter = styled(TwitterSVG)`
  ${Icon}
`

const Instagram = styled(InstagramSVG)`
  ${Icon}
`

const Linkedin = styled(LinkedinSVG)`
  ${Icon}
`
